import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, ApiError } from '../lib/api.js'
import { useAuth } from '../hooks/useAuth.js'
import { Card, Notice, Badge } from '../components/ui.js'
import { ROLE_LABEL, type TeamRole } from '../hooks/useTeam.js'

/**
 * The page an invite link opens.
 *
 * ── Why it renders while signed out instead of redirecting ──────────────────
 *
 * The same reason `/connect/*` does: a redirect to `/login` would drop the
 * token out of the URL, and the person would arrive at a sign-in screen with no
 * idea why, holding a link that is now spent from their point of view. So the
 * preview is fetched first — it needs no session — and the invitation is shown
 * with the account name and role before anybody is asked to sign in.
 *
 * The preview endpoint is deliberately thin: an account name and a role. It
 * tells the holder of a link nothing they are not already being offered.
 *
 * ── Accepting needs a session, and that is on purpose ───────────────────────
 *
 * Redemption attaches an existing account to a merchant. Creating one here
 * instead would make a forwarded link a way to mint users, so the flow is:
 * sign in or sign up as yourself, come back, accept.
 */

interface InvitePreview {
  merchant: string
  role: TeamRole
  email: string | null
}

export function JoinPage(): React.ReactNode {
  const { token = '' } = useParams<{ token: string }>()
  const { user } = useAuth()
  const signedIn = user !== null
  const navigate = useNavigate()

  const [accepting, setAccepting] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  const { data, isPending, error } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api<InvitePreview>(`/v1/team/invites/${token}/preview`),
    retry: false,
  })

  async function accept(): Promise<void> {
    setAccepting(true)
    setFailed(null)

    try {
      await api(`/v1/team/invites/${token}/accept`, { method: 'POST' })

      /*
       * A full reload rather than a client-side navigation.
       *
       * The session now names a different merchant, and every cached query
       * behind it — stores, wallet, orders — belongs to the old one. There is no
       * refresh on the auth context, and inventing a partial one here would
       * leave a tree half-rebuilt against two tenants, which is the shape of
       * bug this codebase has already paid for once.
       */
      window.location.assign('/')
    } catch (cause) {
      setFailed(cause instanceof ApiError ? cause.message : 'That invite could not be accepted.')
      setAccepting(false)
    }
  }

  return (
    <div className="auth">
      <div className="auth__panel" style={{ maxWidth: '28rem' }}>
        {isPending && <Card title="Checking the invite">One moment.</Card>}

        {error && (
          <Card title="This invite is not usable">
            <Notice kind="warn" title="The link did not work">
              {error instanceof ApiError ? error.message : 'That invite link is no longer valid. Ask for a new one.'}
            </Notice>
          </Card>
        )}

        {data && (
          <Card title={`Join ${data.merchant}`}>
            <p style={{ marginTop: 0 }}>
              You have been invited as <Badge tone="info">{ROLE_LABEL[data.role]}</Badge>.
            </p>

            {failed && (
              <Notice kind="warn" title="That did not work">
                {failed}
              </Notice>
            )}

            {signedIn ? (
              <>
                <p>
                  Signed in as <strong>{user?.email ?? user?.phone ?? user?.name}</strong>. Accepting adds this account
                  to {data.merchant}.
                </p>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => void accept()}
                  disabled={accepting}
                >
                  {accepting ? 'Joining…' : `Join ${data.merchant}`}
                </button>
              </>
            ) : (
              <>
                <p>
                  Sign in with your own account to accept
                  {data.email ? <> — the invite was addressed to <strong>{data.email}</strong></> : null}. You will come
                  back here afterwards.
                </p>
                {/*
                  `next` rather than a bare redirect: this URL is the invite, and
                  losing it in the sign-in round trip is losing the invitation.
                */}
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => navigate(`/login?next=${encodeURIComponent(`/join/${token}`)}`)}
                >
                  Sign in to continue
                </button>
              </>
            )}
          </Card>
        )}
      </div>
    </div>
  )
}
